"use strict";
/**
 * This module is a collection of functions to deal with dates and time.
 * Such as formatting, displaying and parsing.
 * Usage:
 * `import { functionName } from 'priority_utils/lib/date'`
 *
 *
 * @module date
 * @preferred
 *
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCurrentAmPm = exports.convertTo14DigitFormat = exports.unShiftUTCDateTimeToCurrentTimeZone = exports.shiftUTCDateTimeToCurrentTimeZone = exports.isValidTime = exports.getRawTime = exports.getRawDate = exports.isValid = exports.momentToString = exports.dayAndMonthAndYearCheck = exports.dayAndMonthCheck = exports.dayOrMonthCheck = exports.validFullYear = exports.isNumber = exports.validNumber = exports.convertTo24HFormat = exports.formatServerDate = exports.dateTime = exports.time = exports.timeFormat = exports.dateToTime = exports.dateToWEBSDK = exports.dateToISO = exports.longDate = exports.date = void 0;
var moment_1 = __importDefault(require("moment"));
/**
 * date takes a date(not supporting timestamp)
 * returns a string to diaplay according to the requested format
 * @param {any} value any moment consructor date such as Date object, ISO string or moment instance
 * @param {string} format a format for display
 */
function date(value, format) {
    if (value && !format && (0, moment_1.default)(value).isValid()) {
        return Intl.DateTimeFormat().format((0, moment_1.default)(value).toDate());
    }
    if (value && format && (0, moment_1.default)(value).isValid()) {
        var formatedDate = (0, moment_1.default)(value).format(format);
        return formatedDate;
    }
    return '';
}
exports.date = date;
/**
 * longDate takes a date
 * and the short date format
 * returns a string to diaplay in a long format (including words)
 * @param {any} value any moment consructor date such as Date object, ISO string or moment instance
 * @param {string} format a format for display
 * @param {string} locale the locale for words in date format
 */
function longDate(value, format, locale) {
    // remove time from format
    var dateFormat = format.split(' ')[0];
    // replace month and year with long format
    var longFormat = dateFormat
        .replace(/MM/, 'MMM')
        .replace(/Y+/, 'YYYY')
        .replace(/\//, ' ')
        .replace(/\//, ', ');
    var mdate = (0, moment_1.default)(value);
    // we need to set the locale, since long format contains words
    mdate.locale(locale);
    if (!mdate.isValid()) {
        return '';
    }
    return mdate.format(longFormat);
}
exports.longDate = longDate;
/**
 * dateToISO converts a date to an ISO string that represents the date
 * @param {any} value any moment consructor date such as Date object, ISO string or moment instance
 */
function dateToISO(value) {
    if (value) {
        return (0, moment_1.default)(value).toISOString();
    }
    return '';
}
exports.dateToISO = dateToISO;
/**
 * dateToWEBSDK converts a date to a string suitable for the web sdk
 * @param {any} value any moment consructor date such as Date object, ISO string or moment instance
 */
function dateToWEBSDK(value, isDateTime) {
    if (value) {
        return isDateTime ? dateToISO(value) : (0, moment_1.default)(value).format('YYYY-MM-DD');
    }
    return '';
}
exports.dateToWEBSDK = dateToWEBSDK;
/**
 * dateToTime converts a date to a time string
 * @param {any} value any moment consructor date such as Date object, ISO string or moment instance
 * @param {boolean} is24Hour should time be formated to 24 hour
 */
function dateToTime(value, is24Hour) {
    var format = timeFormat(is24Hour);
    return (0, moment_1.default)(value).format(format);
}
exports.dateToTime = dateToTime;
/**
 * dateFormat return the requested date format with the time format according to is24Hour boolean
 * @param {string} format date or date + time format (seperated with ' ')
 * @param {boolean} is24Hour should time be formated in 24 hour
 */
function dateTimeFormat(format, is24Hour) {
    var splittedFormat = format.split(' ');
    var newFormat = splittedFormat.length > 1 ? splittedFormat[0] + ' ' + timeFormat(is24Hour) : format;
    return newFormat;
}
/**
 * timeFormat returns the time format according to is24Hour boolean
 * @param is24Hour should time be formated in 24 hour
 */
function timeFormat(is24Hour) {
    return is24Hour ? 'HH:mm' : 'hh:mm A';
}
exports.timeFormat = timeFormat;
/**
 * time function takes a time string and returns it formatted according to is24Hour boolean
 * @param {string} value a time string in "HH:mm" format
 * @param {boolean} is24Hour should time be formated in 24 hour
 */
function time(value, is24Hour) {
    if (value) {
        var format = timeFormat(is24Hour);
        return (0, moment_1.default)(value, 'HH:mm').format(format);
    }
    return '';
}
exports.time = time;
/**
 * time function takes a date and returns a formatted date where the time is formatted according to is24Hour boolean
 * @param {any} value any moment constuctor date such as Date object, ISO string or moment instance
 * @param {string} format the format to display
 * @param {boolean} is24Hour should time be formatted in 24 hour
 */
function dateTime(value, format, is24Hour) {
    var newFormat = dateTimeFormat(format, is24Hour);
    return date(value, newFormat);
}
exports.dateTime = dateTime;
/**
 * takes server date (int) and format and returns the date;
 * @param {number} serverDate int
 * @param {string} format the format to display
 */
function formatServerDate(serverDate, dateFormat) {
    if (serverDate > 0) {
        var rawDate = new Date(serverDate * 60000 + 567993600000);
        return date(rawDate, dateFormat);
    }
    return '';
}
exports.formatServerDate = formatServerDate;
function convertTo24HFormat(value, currentFormat) {
    return value && currentFormat ? (0, moment_1.default)(value, currentFormat).format('HH:mm') : '';
}
exports.convertTo24HFormat = convertTo24HFormat;
/**
 * takes value (string) and dateNumber (number) and returns the boolean;
 * @param {string} value is some number part of formatted value
 * @param {number} dateNumber valid date number for comparisons
 */
function validNumber(value, dateNumber) {
    if (!value) {
        return true;
    }
    var leanNumber = value.replace(/(\*|_|\?)/g, '');
    return Number(leanNumber) <= dateNumber;
}
exports.validNumber = validNumber;
var isNumber = function (value) {
    return /^[-]?\d+$/.test(value) ? Number(value) : 0;
};
exports.isNumber = isNumber;
/**
 * validFullYear takes value (string) and returns boolean;
 * @param {string} value of the year to validate
 */
function validFullYear(value) {
    var yearNumber = Number(value.replace(/(\*|_|\?)/g, ''));
    if (value.length === 0) {
        return true;
    }
    if (value.length === 1) {
        return yearNumber >= 1 && yearNumber <= 2;
    }
    if (value.length === 2) {
        return yearNumber >= 19 && yearNumber <= 21;
    }
    if (value.length === 3) {
        return yearNumber >= 190 && yearNumber <= 210;
    }
    if (value.length === 4) {
        return yearNumber >= 1900 && yearNumber <= 2100;
    }
    return false;
}
exports.validFullYear = validFullYear;
/**
 * dayOrMonthCheck takes value (string) and returns boolean;
 * @param {string[]} dateFormatArray of the year to validate
 * @param {string} dateFormat
 */
function dayOrMonthCheck(dateFormatArray, dateFormat) {
    var calenderFormatArray = dateFormat.split(' ')[0].split('/');
    if (dateFormatArray[0].length > 0) {
        if (calenderFormatArray[0] === 'DD') {
            if (dateFormatArray[0].length === 1) {
                return validNumber(dateFormatArray[0], 3) && dateFormatArray[0] !== '00';
            }
            else if (dateFormatArray[0].length === 2) {
                return validNumber(dateFormatArray[0], 31) && dateFormatArray[0] !== '00';
            }
            return false;
        }
        else if (calenderFormatArray[0] === 'MM') {
            if (dateFormatArray[0].length === 1) {
                return validNumber(dateFormatArray[0], 1) && dateFormatArray[0] !== '00';
            }
            else if (dateFormatArray[0].length === 2) {
                return validNumber(dateFormatArray[0], 12) && dateFormatArray[0] !== '00';
            }
            return false;
        }
        return false;
    }
    return true;
}
exports.dayOrMonthCheck = dayOrMonthCheck;
/**
 * dayAndMonthCheck takes value (string) and returns boolean;
 * @param {string[]} dateFormatArray of the year to validate
 * @param {string} dateFormat
 * @param {Object} maxDay
 */
function dayAndMonthCheck(dateFormatArray, dateFormat, maxDay) {
    var calenderFormatArray = dateFormat.split(' ')[0].split('/');
    if (dateFormatArray[1].length > 0) {
        if (calenderFormatArray[0] === 'DD') {
            return (validNumber(dateFormatArray[0], maxDay[(0, exports.isNumber)(dateFormatArray[1])]) &&
                validNumber(dateFormatArray[0][0], 3) &&
                validNumber(dateFormatArray[1], 12) &&
                validNumber(dateFormatArray[1][0], 1) &&
                dateFormatArray[0] !== '00' &&
                dateFormatArray[1] !== '00');
        }
        else if (calenderFormatArray[0] === 'MM') {
            return (validNumber(dateFormatArray[0], 12) &&
                validNumber(dateFormatArray[0][0], 1) &&
                validNumber(dateFormatArray[1], maxDay[(0, exports.isNumber)(dateFormatArray[0])]) &&
                validNumber(dateFormatArray[1][0], 3) &&
                dateFormatArray[0] !== '00' &&
                dateFormatArray[1] !== '00');
        }
        return false;
    }
    return dayOrMonthCheck(dateFormatArray, dateFormat);
}
exports.dayAndMonthCheck = dayAndMonthCheck;
/**
 * dayAndMonthAndYearCheck takes value (string) and returns boolean;
 * @param {string[]} dateFormatArray of the year to validate
 * @param {string} dateFormat
 * @param {Object} maxDay
 */
function dayAndMonthAndYearCheck(dateFormatArray, dateFormat, maxDay) {
    var calenderFormatArray = dateFormat.split(' ')[0].split('/');
    if (dateFormatArray[2].length > 0) {
        if (calenderFormatArray[2] === 'YY') {
            if (calenderFormatArray[0] === 'DD') {
                return (validNumber(dateFormatArray[0], maxDay[(0, exports.isNumber)(dateFormatArray[1])]) &&
                    validNumber(dateFormatArray[1], 12) &&
                    validNumber(dateFormatArray[2], 99) &&
                    dateFormatArray[2].length <= 2 &&
                    dateFormatArray[0] !== '00' &&
                    dateFormatArray[1] !== '00');
            }
            else if (calenderFormatArray[0] === 'MM') {
                return (validNumber(dateFormatArray[0], 12) &&
                    validNumber(dateFormatArray[1], maxDay[(0, exports.isNumber)(dateFormatArray[0])]) &&
                    validNumber(dateFormatArray[2], 99) &&
                    dateFormatArray[2].length <= 2 &&
                    dateFormatArray[0] !== '00' &&
                    dateFormatArray[1] !== '00');
            }
            return false;
        }
        else if (calenderFormatArray[2] === 'YYYY') {
            if (calenderFormatArray[0] === 'DD') {
                return (validNumber(dateFormatArray[0], maxDay[(0, exports.isNumber)(dateFormatArray[1])]) &&
                    validNumber(dateFormatArray[1], 12) &&
                    validFullYear(dateFormatArray[2]) &&
                    dateFormatArray[2].length <= 4 &&
                    dateFormatArray[0] !== '00' &&
                    dateFormatArray[1] !== '00');
            }
            else if (calenderFormatArray[0] === 'MM') {
                return (validNumber(dateFormatArray[0], 12) &&
                    validNumber(dateFormatArray[1], maxDay[(0, exports.isNumber)(dateFormatArray[1])]) &&
                    validFullYear(dateFormatArray[2]) &&
                    dateFormatArray[2].length <= 4 &&
                    dateFormatArray[0] !== '00' &&
                    dateFormatArray[1] !== '00');
            }
            return false;
        }
        return false;
    }
    return dayAndMonthCheck(dateFormatArray, dateFormat, maxDay);
}
exports.dayAndMonthAndYearCheck = dayAndMonthAndYearCheck;
function momentToString(newMoment, dateFormat) {
    if (newMoment === void 0) { newMoment = ''; }
    return moment_1.default.isMoment(newMoment) ? newMoment.format(dateFormat) : newMoment;
}
exports.momentToString = momentToString;
/**
 * isValid takes dateValue and dateFormat. returns boolean;
 * @param {(string || Object)} dateValue is a date string or moment object
 * @param {string} dateFormat
 */
function isValid(dateValue, dateFormat) {
    if (!dateValue) {
        return true;
    }
    if (moment_1.default.isMoment(dateValue)) {
        return (0, moment_1.default)(dateValue, dateFormat).isValid(); // Could be false if user types not completed date + time value
    }
    var maxDay = {
        1: 31,
        2: 29,
        3: 31,
        4: 30,
        5: 31,
        6: 30,
        7: 31,
        8: 31,
        9: 30,
        10: 31,
        11: 30,
        12: 31,
        0: 31,
    };
    var dataArray = dateValue.split(' ');
    var dateFormatArray = dataArray[0].split('/');
    var dateValidator = false;
    var timeValidator = true;
    var isTimeValidatorNeed = dateFormat &&
        dateFormat.split(' ') &&
        dateFormat.split(' ')[1] &&
        dateValue.split(' ').length > 1 &&
        dateValue.split(' ')[1].length > 0;
    if (isTimeValidatorNeed) {
        var timeVal = dateValue.split(' ')[2]
            ? "".concat(dateValue.split(' ')[1], " ").concat(dateValue.split(' ')[2])
            : dateValue.split(' ')[1];
        var formatTime = dateFormat.split(' ')[2]
            ? "".concat(dateFormat.split(' ')[1], " ").concat(dateFormat.split(' ')[2])
            : dateFormat.split(' ')[1];
        var contentMatchFormat = dateFormat.split(' ').length >= dateValue.split(' ').length;
        timeValidator = isValidTime(timeVal, formatTime) && contentMatchFormat;
    }
    if (dateValue.length === 0) {
        return true;
    }
    if (!dataArray[0].match(/([0-9]|_|)/)) {
        return false;
    }
    if (dateFormatArray.length === 0) {
        dateValidator = true;
    }
    else if (dateFormatArray.length === 1) {
        dateValidator = dayOrMonthCheck(dateFormatArray, dateFormat);
    }
    else if (dateFormatArray.length === 2 && dateFormatArray[0].length === 2) {
        dateValidator = dayAndMonthCheck(dateFormatArray, dateFormat, maxDay);
    }
    else if (dateFormatArray.length === 3 && dateFormatArray[0].length === 2 && dateFormatArray[1].length === 2) {
        dateValidator = dayAndMonthAndYearCheck(dateFormatArray, dateFormat, maxDay);
    }
    return dateValidator && timeValidator;
}
exports.isValid = isValid;
/**
 * getRawDate takes oldValue, value, dateFormat params and returns formatted string;
 * @param {string} oldValue
 * @param {string} value
 * @param {string} dateFormat
 * @param {boolean} isDateTime
 */
function getRawDate(oldValue, value, dateFormat, isDateTime) {
    var newValue = value;
    var typing = value.length > oldValue.length; // Detect the user action typing vs removing value
    var removing = !typing;
    if (value.length === 2 && typing) {
        newValue = "".concat(value, "/");
    }
    else if (value.length === 5 && typing) {
        newValue = "".concat(value, "/");
    }
    else if (value.length === 2 && removing) {
        newValue = value.substring(0, 1);
    }
    else if (value.length === 5 && removing) {
        newValue = value.substring(0, 4);
    }
    var isDateTimeFormat = isDateTime || dateFormat === 'DD/MM/YY HH:mm' || dateFormat === 'MM/DD/YY hh:mm A';
    if (dateFormat === 'MM/DD/YY hh:mm A') {
        dateFormat = 'MM/DD/YY hh:mm AA';
    }
    if (isDateTimeFormat) {
        if (value.length === 8 && typing) {
            newValue = "".concat(value, " ");
        }
        else if (value.length === 11 && typing) {
            newValue = "".concat(value, ":");
        }
        else if (value.length === 14 && dateFormat.length > value.length) {
            if (typing) {
                newValue = "".concat(value, " ");
            }
            else {
                newValue = value.substring(0, 13);
            }
        }
        else if (value.length === 16 && dateFormat.length >= value.length) {
            var lastChar = value[value.length - 1].toLowerCase();
            if (typing && (lastChar === 'a' || lastChar === 'p')) {
                newValue = "".concat(value, "M").toUpperCase();
            }
            else if (removing) {
                newValue = value.substring(0, 15);
            }
        }
        else if (value.length === 9 && oldValue.length === 10) {
            newValue = value.substring(0, 9);
        }
        else if (value.length === 8 && removing) {
            newValue = value.substring(0, 7);
        }
        else if (value.length === 11 && removing) {
            newValue = value.substring(0, 10);
        }
    }
    if (value.length > dateFormat.length) {
        return oldValue;
    }
    return newValue;
}
exports.getRawDate = getRawDate;
function isValid24(value) {
    // check valid characters
    if (value && !value.match(/(\*|_|\?|[0-9:AMPamp\?])/)) {
        return false;
    }
    if (value.indexOf(':') > 1) {
        var valid = true;
        if (value.split(':').length > 2) {
            return false;
        }
        var _a = value.split(':'), hours = _a[0], minutes = _a[1];
        if (hours) {
            valid = hours.length <= 2 && validNumber(hours[0], 2) && validNumber(hours, 23);
        }
        if (minutes && minutes.length) {
            valid = minutes.length <= 2 && validNumber(minutes[0], 5) && validNumber(minutes, 59);
        }
        return valid;
    }
    return value.length <= 2 && validNumber(value[0], 2) && validNumber(value, 24);
}
function isValidAMPM(value) {
    // check valid characters
    if (value && !value.match(/(\*|_|\?|[0-9:AMPamp\?])/)) {
        return false;
    }
    if (value.indexOf(':') > 1) {
        /* check fo only XX:XX format*/
        if (value.split(':').length > 2) {
            return false;
        }
        var valid = true;
        var _a = value.split(':'), hours = _a[0], minutes = _a[1];
        if (hours) {
            valid = hours.length <= 2 && validNumber(hours[0], 1) && validNumber(hours, 12);
        }
        if (minutes) {
            if (value.indexOf(' ') === 5) {
                var _b = minutes.split(' '), minutes2 = _b[0], AM = _b[1];
                if (value.split(' ').length > 2) {
                    return false;
                }
                valid = minutes2.length <= 2 && validNumber(minutes2[0], 5) && validNumber(minutes2, 59);
                valid = valid && AM.length <= 2;
                if (AM) {
                    valid = valid && AM[0].match(/[AaPp]/);
                    if (AM[1]) {
                        valid = valid && AM[1].match(/[mM]/);
                    }
                }
            }
            else {
                valid = valid && minutes.length <= 2 && validNumber(minutes[0], 5) && validNumber(minutes, 59);
            }
        }
        return valid;
    }
    else {
        return value.length <= 2 && validNumber(value[0], 1) && validNumber(value, 12);
    }
}
/**
 * getRawTime takes oldValue, newValue, format params and returns formatted time string;
 * @param {string} newValue
 * @param {string} oldValue
 * @param {string} format
 */
function getRawTime(newValue, oldValue, format) {
    var typing = newValue.length > oldValue.length; // Detect the user action typing vs removing value
    var removing = !typing;
    if (newValue.length === 2 && typing) {
        return "".concat(newValue, ":");
    }
    else if (newValue.length === 2 && removing) {
        return newValue.substring(0, 1);
    }
    if (format !== 'HH:mm') {
        if (newValue.length === 5 && typing) {
            return "".concat(newValue, " ");
        }
        else if (newValue.length === 5 && removing) {
            return newValue.substring(0, 4);
        }
        else if (newValue.length === 7 && typing) {
            return newValue.substring(0, 6) + newValue.charAt(6).toUpperCase() + 'M';
        }
        else if (newValue.length === 7 && removing) {
            return newValue.substring(0, 6);
        }
    }
    return newValue;
}
exports.getRawTime = getRawTime;
/**
 * getRawTime takes value, format and returns boolean;
 * @param {string} value
 * @param {string} format
 */
function isValidTime(value, format) {
    return format === 'HH:mm' ? isValid24(value) : isValidAMPM(value);
}
exports.isValidTime = isValidTime;
function shiftUTCDateTimeToCurrentTimeZone(utcDate) {
    utcDate.setMinutes(utcDate.getMinutes() - utcDate.getTimezoneOffset());
}
exports.shiftUTCDateTimeToCurrentTimeZone = shiftUTCDateTimeToCurrentTimeZone;
function unShiftUTCDateTimeToCurrentTimeZone(utcDate) {
    utcDate.setMinutes(utcDate.getMinutes() + utcDate.getTimezoneOffset());
}
exports.unShiftUTCDateTimeToCurrentTimeZone = unShiftUTCDateTimeToCurrentTimeZone;
function convertTo14DigitFormat(value) {
    if (value.length === 17) {
        var ampm = value.slice(-2);
        if (ampm === 'AM') {
            return value.slice(0, value.length - 2);
        }
        if (ampm === 'PM') {
            var hours = Number.parseInt(value.slice(8, 11), 10) + 12;
            return "".concat(value.substring(0, 9)).concat(hours).concat(value.substring(11, 14));
        }
    }
    return value;
}
exports.convertTo14DigitFormat = convertTo14DigitFormat;
function getCurrentAmPm() {
    return new Date().getHours() >= 12 ? 'PM' : 'AM';
}
exports.getCurrentAmPm = getCurrentAmPm;
